import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PiArrowSquareOutBold, PiBoatBold, PiPackageBold, PiUserBold } from 'react-icons/pi';
import { Badge, Button, LoadingState } from '@v2/design-system';
import V2Modal from '@v2/components/ui/V2Modal';
import api from '@v2/lib/api';
import type { CalendarEvent, ContainerCalendarDetail } from './types';

type Props = {
    event: CalendarEvent | null;
    onClose: () => void;
};

function Field({ label, value }: { label: string; value: React.ReactNode }) {
    if (value == null || value === '' || value === '—') {
        return null;
    }
    return (
        <div>
            <dt className="text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</dt>
            <dd className="mt-0.5 text-sm font-medium text-gray-900 dark:text-gray-100">{value}</dd>
        </div>
    );
}

export default function ContainerDetailModal({ event, onClose }: Props) {
    const { data, isLoading, isError } = useQuery<ContainerCalendarDetail>({
        queryKey: ['calendar', 'container', event?.id],
        enabled: !!event?.id,
        queryFn: async () => (await api.get(`/calendar/containers/${encodeURIComponent(event!.id)}`)).data,
    });

    const open = !!event;

    return (
        <V2Modal
            open={open}
            onClose={onClose}
            size="2xl"
            title={data?.container.container_number ?? event?.title ?? 'Container'}
            description={
                event
                    ? `${event.event_type === 'etd' ? 'ETD' : 'ETA'} · ${event.date} · ${event.subtitle}`
                    : undefined
            }
            footer={
                data?.shipment.v2_url ? (
                    <Button as={Link} to={`/shipments/${data.shipment.id}`} variant="solid" size="sm">
                        Abrir embarque
                        <PiArrowSquareOutBold className="ms-1 size-3.5" />
                    </Button>
                ) : null
            }
        >
            {isLoading && <LoadingState label="Carregando detalhes…" />}
            {isError && <p className="text-sm text-rose-600">Não foi possível carregar os dados.</p>}
            {data && (
                <div className="grid gap-6 lg:grid-cols-2">
                    <section className="space-y-4">
                        <div className="flex items-center gap-2">
                            <PiPackageBold className="size-5 text-sky-600" />
                            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Container</h3>
                            {data.container.is_aggregate && (
                                <Badge color="warning" variant="flat" size="sm" rounded="pill">
                                    Agregado
                                </Badge>
                            )}
                        </div>
                        <dl className="grid gap-3 sm:grid-cols-2">
                            <Field label="Número" value={data.container.container_number} />
                            <Field label="Tipo" value={data.container.type} />
                            <Field label="Lacre" value={data.container.seal_number} />
                            <Field label="CBM" value={data.container.cbm != null ? String(data.container.cbm) : null} />
                            <Field
                                label="Peso bruto"
                                value={
                                    data.container.gross_weight_kg != null
                                        ? `${data.container.gross_weight_kg} kg`
                                        : null
                                }
                            />
                            <Field label="Observações" value={data.container.observations} />
                        </dl>
                        {data.siblings.length > 1 && (
                            <div>
                                <p className="mb-2 text-xs font-semibold uppercase text-gray-500 dark:text-gray-400">
                                    Outros containers do processo
                                </p>
                                <ul className="flex flex-wrap gap-2">
                                    {data.siblings.map((s) => (
                                        <li
                                            key={s.id}
                                            className="rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 font-mono text-xs text-gray-800 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300"
                                        >
                                            {s.container_number}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </section>

                    <section className="space-y-4">
                        <div className="flex items-center gap-2">
                            <PiUserBold className="size-5 text-emerald-600" />
                            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Pedido e cliente</h3>
                        </div>
                        <dl className="grid gap-3 sm:grid-cols-2">
                            <Field label="Pedido" value={data.shipment.order_number} />
                            <Field label="Cliente" value={data.shipment.client} />
                            <Field label="Fornecedor" value={data.shipment.supplier_label} />
                            <Field label="Modalidade" value={data.shipment.modality} />
                            <Field label="Status processo" value={data.shipment.process_status} />
                            <Field label="Estágio" value={data.shipment.stage_label} />
                        </dl>

                        <div className="flex items-center gap-2 pt-2">
                            <PiBoatBold className="size-5 text-violet-600" />
                            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Trânsito marítimo</h3>
                        </div>
                        <dl className="grid gap-3 sm:grid-cols-2">
                            <Field label="Armador" value={data.shipment.shipping_line} />
                            <Field label="Agente" value={data.shipment.cargo_agent} />
                            <Field label="Navio" value={data.shipment.vessel_name ?? data.shipment.vessel_voyage} />
                            <Field label="B/L" value={data.shipment.bl_number} />
                            <Field label="ETD" value={data.shipment.etd_effective ?? data.shipment.etd_origin} />
                            <Field label="ETA" value={data.shipment.eta_effective ?? data.shipment.eta_destination} />
                            <Field label="ETA real" value={data.shipment.eta_actual} />
                            <Field label="Previsão CD" value={data.shipment.estimated_cd_date} />
                            <Field label="Prazo entrega" value={data.shipment.delivery_deadline} />
                        </dl>
                        {(data.shipment.delay_risk_score != null || data.shipment.delay_risk_level) && (
                            <p className="text-xs text-gray-600 dark:text-gray-400">
                                Risco de atraso:{' '}
                                <strong>
                                    {data.shipment.delay_risk_level ?? '—'} ({data.shipment.delay_risk_score ?? 0})
                                </strong>
                            </p>
                        )}
                        {data.etd_eta_task && (
                            <div className="rounded-lg border border-violet-100 bg-violet-50/50 p-3 text-xs dark:border-violet-800/40 dark:bg-violet-950/20">
                                <p className="font-semibold text-violet-900 dark:text-violet-300">{data.etd_eta_task.title}</p>
                                <p className="mt-1 text-violet-800 dark:text-violet-400">
                                    {data.etd_eta_task.is_completed ? 'Concluída' : 'Pendente'}
                                    {data.etd_eta_task.requirement_data.novo_eta != null && (
                                        <>
                                            {' '}
                                            · Novo ETA: {String(data.etd_eta_task.requirement_data.novo_eta)}
                                        </>
                                    )}
                                </p>
                            </div>
                        )}
                    </section>
                </div>
            )}
        </V2Modal>
    );
}
